import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { HTTPError } from 'ky'
import { z } from 'zod'
import { handleResponse } from '../api/helpers.ts'
import { type UserRoleEntity, userManager } from '../api/user-manager.ts'
import { dayjs } from '../dayjs.ts'
import { useImpersonation } from './impersonation.ts'
import { queryData } from './queryHelpers.ts'
import { createInsightUser, roleData, userData } from './users.ts'

export const currentUserData = queryData(() =>
	queryOptions({
		queryKey: ['CURRENT_USER'],
		queryFn: async () => {
			const user = await userManager.GET('/user-manager/users/me').then(handleResponse)
			const roles = await roleData.ensure()
			const currentUser = createInsightUser(user, roles)
			if (!currentUser) throw new Error(`Could not identify user ${user.id}`)
			return currentUser
		},
		staleTime: 1000 * 60,
	}),
)

export const languages = ['EN', 'ES', 'pt-BR'] as const
export const preferenceOptions = {
	unitsAirPressure: ['HECTOPASCALS', 'MILLIBARS', 'INCH_OF_MERCURY'],
	unitsAirQuality: ['EEA', 'USAQI'],
	dateFormat: ['DAY_MONTH_YEAR', 'MONTH_DAY_YEAR'],
	unitsDistance: ['KILOMETRES', /*'METRES',*/ 'MILES', 'NAUTICAL_MILES'],
	notification: ['ALL', 'NONE'],
	lightningRings: ['ALARM_AND_WARNING', 'ALL_RINGS'],
	unitsOtherGases: ['PARTS_PER_MILLION', 'PARTS_PER_BILLION', 'MICROGRAMS_PER_CUBIC_METRE'],
	unitsRain: ['MILLIMETRES', 'INCHES'],
	unitsAirTemperature: ['DEGREES_CELSIUS', 'DEGREES_FAHRENHEIT'],
	unitsWindSpeed: ['METRES_PER_SECOND', 'MILES_PER_HOUR', 'KNOTS', 'KILOMETRES_PER_HOUR'],
	theme: ['LIGHT', 'DARK', 'SYSTEM'],
} as const
export const UserPreferencesSchema = z.object({
	unitsAirTemperature: z.enum(preferenceOptions.unitsAirTemperature),
	unitsWindSpeed: z.enum(preferenceOptions.unitsWindSpeed),
	unitsAirPressure: z.enum(preferenceOptions.unitsAirPressure),
	unitsRain: z.enum(preferenceOptions.unitsRain),
	unitsOtherGases: z.enum(preferenceOptions.unitsOtherGases),
	unitsAirQuality: z.enum(preferenceOptions.unitsAirQuality),
	unitsDistance: z.enum(preferenceOptions.unitsDistance),
	notificationsEmail: z.enum(preferenceOptions.notification),
	notificationsSms: z.enum(preferenceOptions.notification),
	notificationsSound: z.enum(preferenceOptions.notification),
	notificationsSoundLightningRings: z.enum(preferenceOptions.lightningRings),
	notificationsSnoozeSms: z.string(),
	dateFormat: z.enum(preferenceOptions.dateFormat),
	theme: z.enum(preferenceOptions.theme),
	language: z.enum(languages),
	timezone: z.string(),
})
export type UserPreferences = z.infer<typeof UserPreferencesSchema>

export const userPreferencesQuery = queryOptions({
	queryKey: ['USER_PREFERENCES'],
	queryFn: () =>
		userManager.GET('/user-manager/user-preferences/me').then(({ error, data }) => {
			if (error) throw error
			return UserPreferencesSchema.parse(data)
		}),
	staleTime: 1000 * 60 * 5,
})

export const usePreferences = () => useSuspenseQuery(userPreferencesQuery)

/** The user's preferred timezone, falling back to the browser's */
export const useTimezone = () => usePreferences().data?.timezone ?? dayjs.tz.guess()

export const useUpdatePreferences = () => {
	const queryClient = useQueryClient()
	return useMutation({
		// @ts-expect-error: type definition is missing from user manager openapi schema
		mutationFn: (body: UserPreferences) => userManager.PATCH('/user-manager/user-preferences/me', { body }),
		onMutate: async (preferences) => {
			await queryClient.cancelQueries({ queryKey: ['USER_PREFERENCES'] })
			const previousPreferences = queryClient.getQueryData(['USER_PREFERENCES'])
			queryClient.setQueryData(['USER_PREFERENCES'], () => preferences)
			return { previousPreferences }
		},
		onError: (error, _preferences, context) => {
			console.error(error)
			queryClient.setQueryData(['USER_PREFERENCES'], context?.previousPreferences)
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({ queryKey: ['USER_PREFERENCES'] })
		},
	})
}

export const UserProfileUpdateSchema = z.object({
	name: z.string(),
	phone: z.string().optional(),
})
export type UserProfileUpdate = z.infer<typeof UserProfileUpdateSchema>

export const useUpdateProfile = () => {
	const impersonation = useImpersonation()
	return useMutation({
		mutationFn: (body: UserProfileUpdate) => userManager.PATCH('/user-manager/users/me', { body }),
		onSettled: async () => {
			await currentUserData.invalidate()
			await userData.invalidate(impersonation)
		},
	})
}

export const useStartPhoneVerification = () =>
	useMutation({
		mutationFn: (phone: string) => userManager.POST('/user-manager/users/verification/phone', { body: { phone } }),
	})
export const useVerifyPhoneNumber = () =>
	useMutation({
		mutationFn: ({ phone, otp }: { phone: string; otp: string }) =>
			userManager.POST('/user-manager/users/verification/phone/verify', { body: { phone, otp } }),
	})
export const useStartEmailVerification = () =>
	useMutation({
		mutationFn: (userId: string) => userManager.POST('/user-manager/users/verification/email', { body: { userId } }),
	})
export const useVerifyEmail = () =>
	useMutation({
		mutationFn: (secret: string) =>
			userManager
				.POST('/user-manager/users/verification/email/verify', { body: { secret } })
				.then(handleResponse)
				.catch(async (e) => {
					if (e instanceof HTTPError) {
						throw await e.response.json<{ code: string }>()
					}
					throw e
				}),
	})

export const hasRequiredRole = (userRoleName: UserRoleEntity, requiredRoleName: UserRoleEntity['name'] | UserRoleEntity['name'][]) =>
	userRoleName.name === 'SUPER_ADMIN' ||
	(Array.isArray(requiredRoleName) && requiredRoleName.includes(userRoleName.name)) ||
	userRoleName.name === requiredRoleName
